import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { SectionHead } from "@/components/landing/Section";
import { RichText } from "@/components/pages/PageShell";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { SignupFields, fieldCls } from "@/components/signup/SignupFields";
import { PinMap } from "@/components/signup/PinMap";
import { OrderSummary, SignupDone } from "@/components/signup/OrderParts";
import { PlaceDetails } from "@/components/signup/PlaceDetails";
import { HostDone } from "@/components/signup/HostDone";
import { PLACE_ROLES, ROLE_PACKAGE, SIGNUP_ROLES } from "@/config/business";
import { pkgByKey } from "@/config/supporter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+()\-\s]{5,30}$/;
const EMPTY = { role: "", firstName: "", lastName: "", company: "", email: "", phone: "", website: "", pkg: "", offer: "", amenities: [], message: "", consent: false, nickname: "" };

const usePrefill = (setForm) => {
  const [params] = useSearchParams();
  useEffect(() => {
    const role = params.get("role");
    const p = params.get("package");
    setForm((f) => {
      const next = { ...f };
      if (SIGNUP_ROLES.includes(role)) {
        next.role = role;
        if (ROLE_PACKAGE[role]) next.pkg = ROLE_PACKAGE[role];
      }
      if (pkgByKey(p)) next.pkg = p;
      return next;
    });
  }, [params, setForm]);
};

const toPayload = (form, pins, lang) => ({
  role: form.role, first_name: form.firstName.trim(), last_name: form.lastName.trim(), company: form.company.trim(),
  email: form.email.trim(), phone: form.phone.trim(), website: form.website.trim(), package: form.pkg,
  pins: pins.map(({ lat, lng }) => ({ lat, lng })), offer: form.offer.trim(), amenities: form.amenities,
  message: form.message.trim(), consent: form.consent, lang, nickname: form.nickname,
});

export default function SignupSection() {
  const { t, lang } = useLang();
  const s = t.pages.signup;
  const [form, setForm] = useState(EMPTY);
  const [pins, setPins] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);
  usePrefill(setForm);
  const pkg = pkgByKey(form.pkg);
  const max = pkg?.trolls || 0;
  const isHost = !!pkg?.free;
  const showPlace = isHost || PLACE_ROLES.includes(form.role);
  useEffect(() => {
    if (max) setPins((ps) => ps.slice(0, max));
  }, [max]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onRole = (role) => role && setForm((f) => ({ ...f, role, pkg: f.pkg || ROLE_PACKAGE[role] || "" }));

  const validate = () => {
    if (!form.role || !form.firstName.trim() || !form.lastName.trim() || !form.pkg) return s.errors.required;
    if (!EMAIL_RE.test(form.email.trim())) return s.errors.email;
    if (!PHONE_RE.test(form.phone.trim())) return s.errors.phone;
    if (!pins.length) return s.errors.pins;
    if (isHost && !form.offer.trim()) return s.offerError;
    if (!form.consent) return s.errors.consent;
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    setError(err);
    if (err) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/signups`, toPayload(form, pins, lang));
      setOrder({ ...data, host: data.package === "host", firstName: form.firstName.trim(), email: form.email.trim() });
      setTimeout(() => document.getElementById("signup")?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (ex) {
      const code = ex.response?.status;
      setError(code === 429 ? s.errors.tooMany : code === 422 ? s.errors.required : s.errors.generic);
    }
    setSending(false);
  };

  const reset = () => {
    setForm(EMPTY);
    setPins([]);
    setOrder(null);
  };

  return (
    <section id="signup" data-testid="signup-section" className="scroll-mt-24">
      <SectionHead kicker={s.kicker} title={s.title} body={s.lead} />
      {order ? (
        order.host ? <HostDone order={order} onAnother={reset} /> : <SignupDone order={order} onAnother={reset} />
      ) : (
        <form onSubmit={submit} noValidate data-testid="signup-form" className="mt-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-10">
            <SignupFields form={form} set={set} onRole={onRole} />
            <PinMap pins={pins} setPins={setPins} max={max} host={isHost} />
            {showPlace && <PlaceDetails form={form} set={set} required={isHost} />}
            <div className="space-y-2">
              <Label htmlFor="signup-message" className="font-bold text-ink">
                {s.message} <span className="font-semibold text-ink-muted">({s.optional})</span>
              </Label>
              <Textarea id="signup-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={s.messagePh} data-testid="signup-message" className={fieldCls} />
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.nickname} onChange={(e) => set("nickname", e.target.value)} className="hidden" />
            <div className="flex items-start gap-3">
              <Checkbox id="signup-consent" checked={form.consent} onCheckedChange={(v) => set("consent", v === true)} data-testid="signup-consent" className="mt-0.5 border-ink data-[state=checked]:bg-moss data-[state=checked]:border-moss" />
              <Label htmlFor="signup-consent" className="text-sm font-semibold text-ink-muted leading-relaxed">
                <RichText text={s.consent} />
              </Label>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <OrderSummary pkg={pkg} placed={pins.length} sending={sending} error={error} />
            </div>
          </aside>
        </form>
      )}
    </section>
  );
}
